import { Button, Typography } from '@breetometer/components';
import { InfoIcon } from 'BreetAssets';
import { Checkbox, EngagementActions } from 'BreetComponents';
import { copyText } from 'BreetHelpers';
import { selectUserData, useAppSelector } from 'BreetRedux';
import { type ChangeEvent, useCallback, useState } from 'react';

import { type IAuthenticatorBackupCodeProps } from '../Dialog2faSettings.helpers';

const AuthenticatorBackupCode = ({ onNext, recoveryCodes }: IAuthenticatorBackupCodeProps) => {
	const user = useAppSelector(selectUserData);

	const [isBackupCodeSaved, setIsBackupCodeSaved] = useState<boolean>(false);

	const downloadRecoveryCodes = (codes: string[], meta: { email?: string }) => () => {
		const header = [
			'Breet Recovery Codes',
			meta.email ? `Account: ${meta.email}` : undefined,
			`Generated: ${new Date().toISOString()}`,
			'',
			'Each code can be used once. Store this file securely.',
			'',
		]
			.filter(Boolean)
			.join('\n');

		const body = `${header}\n${codes.join('\n')}\n`;
		const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `breet-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);

		setIsBackupCodeSaved(true);
	};

	const copyAllRecoveryCodes = (codes: string[]) => () => {
		const text = codes.join('\n');
		copyText(text, 'Recovery codes copied to clipboard').catch(console.error);
	};

	const handleCheckboxAttestation = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setIsBackupCodeSaved(e.target.checked);
	}, []);

	return (
		<div className='authenticator-backup-code'>
			<Typography
				as='h2'
				className='color-neutral-50'
			>
				Save your codes
			</Typography>

			<div className='backup-code-info border-dark-navy bg-primary-400'>
				<InfoIcon className='svg-dark' />
				<Typography className='color-primary-50'>
					These backup codes can be used once each to access your account if 2-step authentication fails—store them securely.
				</Typography>
			</div>

			<div className='backup-codes border-dark-navy bg-primary-400'>
				{recoveryCodes.map((code) => (
					<div
						key={code}
						className='backup-codes-item'
					>
						<Typography className='border-dark-navy bg-primary-500 color-tertiary-400'>{code}</Typography>
					</div>
				))}
			</div>

			<div className='qr-share'>
				<EngagementActions
					noReport
					isCopy
					copy={{
						item: '',
						message: 'Recovery Codes copied to clipboard',
						copyFn: copyAllRecoveryCodes(recoveryCodes),
					}}
					shareContentDetails={{
						title: '2fa backup codes',
						action: 'download',
						downloadFn: downloadRecoveryCodes(recoveryCodes, { email: user?.email }),
					}}
				/>
			</div>

			<div className='backup-code-attestation'>
				<Checkbox
					checked={isBackupCodeSaved}
					onChange={handleCheckboxAttestation}
				>
					<Typography
						as='p'
						variant='sub_head'
						className='color-tertiary-400'
					>
						I have saved these codes in a safe place.
					</Typography>
				</Checkbox>
			</div>

			<div className='qr-cta'>
				<div className='qr-learn-more'>
					<InfoIcon className='svg-dark' />
					<Typography className='color-tertiary-400'>Learn More</Typography>
				</div>

				<Button
					onClick={onNext}
					disabled={!isBackupCodeSaved}
				>
					Complete Setup
				</Button>
			</div>
		</div>
	);
};

export default AuthenticatorBackupCode;
